import { useState } from 'react';
import { PITCH_VIEW_OPTIONS, metresToFeet, feetToMetres, type PitchView, type CustomPitch } from './pitchArea';
import { glassPanel, glassButton, glassInput } from './liquidGlass';

interface PitchSizePickerProps {
  view: PitchView;
  custom: CustomPitch | null;
  onChange: (view: PitchView, custom: CustomPitch | null) => void;
}

/**
 * Compact dropdown popover for picking the drill's working area on
 * the canvas — full / half / quarter or a custom width × length in
 * metres or feet. Storage is metric; the unit toggle is display-only.
 */
export function PitchSizePicker({ view, custom, onChange }: PitchSizePickerProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState<CustomPitch>(custom ?? { width: 30, length: 40, unit: 'm' });

  const currentLabel = (() => {
    if (view === 'custom' && custom) {
      const w = custom.unit === 'ft' ? metresToFeet(custom.width) : custom.width;
      const l = custom.unit === 'ft' ? metresToFeet(custom.length) : custom.length;
      return `${Math.round(w)}×${Math.round(l)} ${custom.unit}`;
    }
    return PITCH_VIEW_OPTIONS.find((o) => o.id === view)?.label ?? 'Full pitch';
  })();

  const handleSelect = (next: PitchView) => {
    if (next === 'custom') {
      // Stay open to show the input. Commit on Apply.
      return;
    }
    onChange(next, null);
    setOpen(false);
  };

  const handleApplyCustom = () => {
    // Always persist as metres regardless of input unit.
    const wM = customDraft.unit === 'ft' ? feetToMetres(customDraft.width) : customDraft.width;
    const lM = customDraft.unit === 'ft' ? feetToMetres(customDraft.length) : customDraft.length;
    onChange('custom', { width: wM, length: lM, unit: customDraft.unit });
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={glassButton}
        title="Choose the drill's working area on the pitch"
      >
        🟩 Pitch · {currentLabel}
      </button>
      {open && (
        <div style={{ ...glassPanel, position: 'absolute', top: 'calc(100% + 6px)', left: 0, padding: 12, minWidth: 280, zIndex: 50 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {PITCH_VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelect(opt.id)}
                style={{
                  ...glassButton,
                  background: view === opt.id ? 'var(--glass-bg-active)' : 'var(--glass-bg-hover)',
                  textAlign: 'left',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                aria-pressed={view === opt.id}
              >
                <span aria-hidden="true">{glyphFor(opt.id)}</span>
                {opt.label}
              </button>
            ))}
          </div>
          {/* Custom dimensions input */}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Custom dimensions
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 56px', gap: 6, alignItems: 'center' }}>
              <label style={fieldStyle}>
                <span style={subLabelStyle}>Width</span>
                <input
                  type="number"
                  value={customDraft.width}
                  onChange={(e) => setCustomDraft({ ...customDraft, width: Number(e.target.value) })}
                  style={glassInput}
                />
              </label>
              <label style={fieldStyle}>
                <span style={subLabelStyle}>Length</span>
                <input
                  type="number"
                  value={customDraft.length}
                  onChange={(e) => setCustomDraft({ ...customDraft, length: Number(e.target.value) })}
                  style={glassInput}
                />
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={subLabelStyle}>Unit</span>
                <div style={{ display: 'flex', gap: 2 }}>
                  {(['m', 'ft'] as const).map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setCustomDraft({ ...customDraft, unit: u })}
                      style={{
                        ...glassButton,
                        flex: 1,
                        padding: '4px 8px',
                        fontSize: 11,
                        background: customDraft.unit === u ? 'var(--glass-bg-active)' : 'var(--glass-bg-hover)',
                      }}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleApplyCustom}
              style={{ ...glassButton, marginTop: 8, width: '100%', background: 'var(--glass-bg-active)' }}
            >
              Apply {customDraft.width}×{customDraft.length} {customDraft.unit}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function glyphFor(v: PitchView): string {
  switch (v) {
    case 'full': return '⬜';
    case 'half_attacking': return '◑';
    case 'half_defending': return '◐';
    case 'quarter': return '◰';
    case 'custom': return '✎';
  }
}

const fieldStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 2,
};

const subLabelStyle: React.CSSProperties = {
  fontSize: 10, opacity: 0.6,
  textTransform: 'uppercase', letterSpacing: 0.4,
};
